import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Popover, Stack, Tooltip } from "@mui/material";
import { RowMenu } from "components/icons";

/**
 * A compact "action tray" for a data-grid row. Renders a single "more" (⋯) button;
 * clicking it opens a small popover with the row's applicable action icons
 * (Edit, Copy, Delete, ...). Selecting an action closes the tray and runs its
 * handler, so the usual flow continues unchanged.
 *
 * Pass only the actions that apply to the row — the tray hides itself when the
 * list is empty.
 *
 * actions: Array<{
 *   key: string,
 *   label: string,        // used as aria-label and tooltip
 *   icon: ReactNode,
 *   onClick?: () => void, // omitted for info-only (disabled) entries
 *   color?: string,       // MUI IconButton color (e.g. "error" for delete)
 *   disabled?: boolean,   // info-only indicator (e.g. "Default Approved"); not clickable
 * }>
 */
export const RowActionsTray = ({ actions = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const applicable = actions.filter(Boolean);
  if (applicable.length === 0) return null;

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleAction = (onClick) => (event) => {
    event.stopPropagation();
    handleClose();
    onClick();
  };

  return (
    <>
      <IconButton
        aria-label="Actions"
        onClick={handleOpen}
        className="row-action-button"
        size="small"
      >
        <RowMenu />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "background.paper",
              boxShadow: 3,
              borderRadius: 1,
              overflow: "visible",
              // Gap + arrowhead on the left edge pointing back at the "more"
              // button that opened this tray.
              ml: "10px",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: 0,
                width: 10,
                height: 10,
                backgroundColor: "background.paper",
                transform: "translate(-50%, -50%) rotate(45deg)",
                boxShadow: "-2px 2px 2px rgba(0, 0, 0, 0.1)",
              },
            },
          },
        }}
      >
        <Stack direction="row" spacing={0.5} sx={{ px: 0.5, py: 0.25 }}>
          {applicable.map((action) =>
            // Info-only entries (e.g. "Default Approved") render a non-clickable icon.
            // A <span> wrapper lets the Tooltip still show over a disabled button.
            action.disabled ? (
              <Tooltip key={action.key} title={action.label}>
                <span>
                  <IconButton aria-label={action.label} className="row-action-button" size="small" disabled>
                    {action.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip key={action.key} title={action.label}>
                <IconButton
                  aria-label={action.label}
                  onClick={handleAction(action.onClick)}
                  // Grey at rest; destructive actions turn red only under the pointer (globals.js).
                  className={`row-action-button${action.color === "error" ? " row-action-button--danger" : ""}`}
                  size="small"
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            )
          )}
        </Stack>
      </Popover>
    </>
  );
};

RowActionsTray.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node.isRequired,
      onClick: PropTypes.func, // required for clickable entries; omit for info-only (disabled)
      color: PropTypes.string,
      disabled: PropTypes.bool,
    })
  ),
};

export default RowActionsTray;
